import { useCallback, useContext } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { useInventoryEventDetailContext } from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useEventMapConfigMetadata } from 'src/hooks/api/useEventMapConfigMetadata';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EventPricingSeatMap } from 'src/modals/EventPricingSeatMap';
import { ConfigureIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

import { EventVenueMapModal } from '../../../EventVenueMap/EventVenueMapModal';
import { EventVenueMapPopup } from '../../../EventVenueMap/EventVenueMapPopup';
import { SidePanelTab } from '../../../MarketListings/MarketListingHeader';
import * as styles from './MapHeader.css';

type MapHeaderProps = {
  useVerticalView: boolean;
};

export const MapHeader: React.FC<MapHeaderProps> = ({ useVerticalView }) => {
  const { event, selectedTab, onTabClick } = useInventoryEventDetailContext();
  const { setModal } = useContext(ModalContext);

  const marketListingsText = useContent(ContentId.MarketListings);
  const salesText = useContent(ContentId.Sales);
  const purchasesText = useContent(ContentId.Purchases);
  const notesText = useContent(ContentId.Notes);
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const tabs = [
    { label: marketListingsText, value: SidePanelTab.LISTINGS },
    { label: salesText, value: SidePanelTab.SALES },
    { label: purchasesText, value: SidePanelTab.PURCHASES },
    { label: notesText, value: SidePanelTab.NOTES },
  ];

  const openEventMapView = useCallback(() => {
    setModal({
      children: <EventVenueMapModal event={event!} />,
      size: 'fullscreen',
    });
  }, [event, setModal]);

  const { eventMapConfigQuery } = useEventMapConfigMetadata(
    event?.viagId ?? null
  );

  return (
    <div className={styles.headerContainer}>
      <Stack alignItems="center" gap="s">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => onTabClick(tab.value)}
            className={`${styles.tabButton} ${
              selectedTab === tab.value ? styles.activeTab : ''
            }`}
          >
            {tab.label}
          </button>
        ))}
      </Stack>

      {event && selectedTab == SidePanelTab.LISTINGS && (
        <Stack gap="s" alignItems="center">
          {event && (
            <EventVenueMapPopup
              onClick={openEventMapView}
              eventMapConfigQuery={eventMapConfigQuery}
              inCondensedView
              iconOnly={useVerticalView}
            />
          )}

          {hasAutoPricingFeature && (
            <ConfigureIcon
              withHoverEffect
              size={vars.iconSize.xl}
              className={styles.configureIcon}
              onClick={() =>
                setModal({
                  children: <EventPricingSeatMap event={event} />,
                  clickOutsideToClose: true,
                  size: 'slide-in',
                  keyboard: false,
                })
              }
            />
          )}
        </Stack>
      )}
    </div>
  );
};
